import moment from 'moment';

import { getFeatureLabel, humanizeKey } from './feature-catalog';

// Différences champ par champ entre deux versions de la configuration d'un
// client, sur le modèle de la correction des envois AS400 (page « Statut
// API ») : on aplatit les deux objets, puis on compare chemin par chemin.

const isPlainObject = (value) =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

const flatten = (value, prefix = '', result = {}) => {
  if (isPlainObject(value) && Object.keys(value).length > 0) {
    Object.entries(value).forEach(([key, child]) =>
      flatten(child, prefix ? `${prefix}.${key}` : key, result)
    );
  } else if (prefix) {
    result[prefix] = value;
  }
  return result;
};

/**
 * @returns {{ path: string, type: 'added'|'removed'|'changed', before?: any, after?: any }[]}
 */
export const computeJsonDiff = (before, after) => {
  const flatBefore = flatten(before ?? {});
  const flatAfter = flatten(after ?? {});
  const paths = new Set([
    ...Object.keys(flatBefore),
    ...Object.keys(flatAfter),
  ]);

  const diff = [];
  paths.forEach((path) => {
    const inBefore = Object.prototype.hasOwnProperty.call(flatBefore, path);
    const inAfter = Object.prototype.hasOwnProperty.call(flatAfter, path);

    if (inBefore && !inAfter) {
      diff.push({ path, type: 'removed', before: flatBefore[path] });
    } else if (!inBefore && inAfter) {
      diff.push({ path, type: 'added', after: flatAfter[path] });
    } else if (
      JSON.stringify(flatBefore[path]) !== JSON.stringify(flatAfter[path])
    ) {
      diff.push({
        path,
        type: 'changed',
        before: flatBefore[path],
        after: flatAfter[path],
      });
    }
  });

  return diff.sort((a, b) => a.path.localeCompare(b.path));
};

const FIELD_LABELS = {
  name: 'Nom du client',
  siret: 'SIRET',
  tva: 'N° de TVA',
  street: 'Adresse',
  zipcode: 'Code postal',
  city: 'Ville',
  country: 'Pays',
  is_email_sending_enabled: 'Envoi d’emails',
  is_sms_sending_enabled: 'Envoi de SMS',
  logo_url: 'Logo',
  features_v2: 'Options',
  pricing: 'Tarification',
  price_per_vehicle: 'Prix par véhicule',
  price_per_email: 'Prix par email',
  price_per_sms: 'Prix par SMS',
  price_per_signature: 'Prix par signature',
  first_name: 'Prénom',
  last_name: 'Nom',
  email: 'E-mail',
  phone_number: 'Téléphone',
  invitation_sent: 'Invitation envoyée',
  type: 'Type',
  file_name: 'Fichier',
  mime_type: 'Format',
  size: 'Taille',
};

/**
 * Libellé lisible d'un chemin : « Options › Étapes › Afficher l’heure
 * estimée › Activé » pour `features_v2.TOUR_STEP.DISPLAY_ETA.enabled`.
 * Les libellés des options viennent du catalogue servi par l'API.
 * `stripRoot` retire le premier segment (« Options », « Tarification »)
 * quand les modifications sont déjà regroupées par section.
 */
export const getPathLabel = (path, catalog, { stripRoot = false } = {}) => {
  const segments = path.split('.');
  const isFeature = segments[0] === 'features_v2';

  const labels = segments.map((segment, index) =>
    isFeature && index > 0
      ? getFeatureLabel(catalog, segments.slice(1, index + 1))
      : FIELD_LABELS[segment] || humanizeKey(segment)
  );

  return (stripRoot && segments.length > 1 ? labels.slice(1) : labels).join(
    ' › '
  );
};

/**
 * Sections de la fiche client, dans l'ordre d'affichage. Sert à regrouper
 * les modifications (récapitulatif avant enregistrement, pastilles de la
 * navigation).
 */
export const CLIENT_SECTION = {
  INFORMATION: 'information',
  OPTIONS: 'options',
  PRICING: 'pricing',
};

export const CLIENT_SECTION_LABELS = {
  [CLIENT_SECTION.INFORMATION]: 'Informations',
  [CLIENT_SECTION.OPTIONS]: 'Options',
  [CLIENT_SECTION.PRICING]: 'Tarification',
};

export const getChangeSection = (path) => {
  if (path.startsWith('features_v2')) return CLIENT_SECTION.OPTIONS;
  if (path.startsWith('pricing')) return CLIENT_SECTION.PRICING;
  return CLIENT_SECTION.INFORMATION;
};

/**
 * @returns {{ section: string, label: string, changes: object[] }[]} les
 *   sections ayant au moins une modification, dans l'ordre de la fiche
 */
export const groupDiffBySection = (diff) =>
  Object.values(CLIENT_SECTION)
    .map((section) => ({
      section,
      label: CLIENT_SECTION_LABELS[section],
      changes: diff.filter(
        (change) => getChangeSection(change.path) === section
      ),
    }))
    .filter((group) => group.changes.length > 0);

export const formatDiffValue = (value) => {
  if (value === undefined) return '—';
  if (value === null) return 'vide';
  if (value === true) return 'oui';
  if (value === false) return 'non';
  if (Array.isArray(value)) return `[${value.join(', ')}]`;
  if (isPlainObject(value)) return '{}';
  return typeof value === 'string' ? `« ${value} »` : String(value);
};

export const HISTORY_EVENT_LABELS = {
  CLIENT_CREATED: 'Création du client',
  CLIENT_UPDATED: 'Modification de la fiche',
  LOGO_UPDATED: 'Logo',
  VROOM_CONFIGURATION_CREATED: 'Configuration VROOM par défaut',
  DOCUMENT_TEMPLATE_ADDED: 'Template déposé',
  DOCUMENT_TEMPLATE_ARCHIVED: 'Template supprimé',
  ADMIN_USER_CREATED: 'Administrateur du client créé',
  ADMIN_USER_ARCHIVED: 'Administrateur du client supprimé',
};

export const formatDateTime = (date) =>
  date ? moment(date).format('DD/MM/YYYY HH:mm') : '—';
